import { normalizeTaskTrackerStickerFilterValues } from './taskTrackerBoard.stickerFilters.utils'
import {
  parseRecurringTaskDeadlineDefaultValue,
  serializeRecurringTaskDeadlineDefaultValue,
} from './taskTrackerRecurringTaskDeadline.utils'
import {
  normalizeStickerValueForApi,
  normalizeTaskTrackerStickerValueForPersist,
} from './taskTrackerStickerSave.utils'
import type { TaskTrackerBoardFilterStickerRow } from '../store/domain.types'
import type {
  RecurringTaskStickerDefaultRow,
  RecurringTaskStickerDefaultValue,
  TaskTrackerRecurringTaskStickerDefault,
} from './taskTrackerRecurringTasks.types'

export const createRecurringTaskStickerDefaultRowId = () =>
  `recurring-task-sticker-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export const createEmptyRecurringTaskStickerDefaultRow =
  (): RecurringTaskStickerDefaultRow => ({
    id: createRecurringTaskStickerDefaultRowId(),
    fieldSettingId: null,
    value: null,
  })

const hasRecurringTaskStickerDefaultValue = (
  fieldType: string,
  value: RecurringTaskStickerDefaultValue,
) => {
  // Для системных/незаполняемых типов в модалке нет editable-контрола значения.
  // В таких строках не требуем обязательный value на фронте.
  if (
    fieldType !== 'text' &&
    fieldType !== 'number' &&
    fieldType !== 'deadline' &&
    fieldType !== 'date' &&
    fieldType !== 'select' &&
    fieldType !== 'performer'
  ) {
    return true
  }

  if (fieldType === 'performer') {
    return normalizeTaskTrackerStickerFilterValues(
      Array.isArray(value) ? value : value ? [value] : [],
    ).length > 0
  }

  if (fieldType === 'deadline') {
    return (
      parseRecurringTaskDeadlineDefaultValue(
        typeof value === 'string' ? value : null,
      ) != null
    )
  }

  if (Array.isArray(value)) return false

  return normalizeTaskTrackerStickerValueForPersist(fieldType, value) != null
}

export const mapRecurringTaskStickerDefaultsToFormRows = (
  defaults: TaskTrackerRecurringTaskStickerDefault[],
): RecurringTaskStickerDefaultRow[] =>
  defaults.map((item) => ({
    id: createRecurringTaskStickerDefaultRowId(),
    fieldSettingId: item.fieldSettingId,
    value:
      item.values && item.values.length > 0
        ? [...item.values]
        : item.value,
  }))

const mapRecurringTaskStickerDefaultRowToSaveItem = (
  row: RecurringTaskStickerDefaultRow,
  sticker: TaskTrackerBoardFilterStickerRow | undefined,
): TaskTrackerRecurringTaskStickerDefault | null => {
  if (row.fieldSettingId == null) return null

  if (sticker?.fieldType === 'performer') {
    const values = normalizeTaskTrackerStickerFilterValues(
      Array.isArray(row.value) ? row.value : row.value ? [row.value] : [],
    )

    if (values.length === 0) return null

    return {
      fieldSettingId: row.fieldSettingId,
      value: null,
      values,
    }
  }

  const scalarValue = Array.isArray(row.value) ? row.value[0] ?? null : row.value

  if (sticker?.fieldType === 'deadline') {
    const offsetDays = parseRecurringTaskDeadlineDefaultValue(scalarValue)
    if (offsetDays == null) return null

    return {
      fieldSettingId: row.fieldSettingId,
      value: serializeRecurringTaskDeadlineDefaultValue(offsetDays),
    }
  }

  const normalized = sticker
    ? normalizeTaskTrackerStickerValueForPersist(sticker.fieldType, scalarValue)
    : normalizeStickerValueForApi(scalarValue)

  if (!normalized) return null

  return {
    fieldSettingId: row.fieldSettingId,
    value: normalized,
  }
}

export const mapRecurringTaskStickerFormRowsToSavePayload = (
  rows: RecurringTaskStickerDefaultRow[],
  stickerRows: TaskTrackerBoardFilterStickerRow[],
): TaskTrackerRecurringTaskStickerDefault[] => {
  const stickerById = new Map(
    stickerRows.map((sticker) => [sticker.fieldSettingId, sticker]),
  )

  return rows
    .map((row) =>
      mapRecurringTaskStickerDefaultRowToSaveItem(
        row,
        row.fieldSettingId == null
          ? undefined
          : stickerById.get(row.fieldSettingId),
      ),
    )
    .filter((item): item is TaskTrackerRecurringTaskStickerDefault => item != null)
}

export const validateRecurringTaskStickerDefaults = (
  rows: RecurringTaskStickerDefaultRow[],
  stickerRows: TaskTrackerBoardFilterStickerRow[],
): string | null => {
  const usedFieldSettingIds = new Set<number>()

  for (const row of rows) {
    if (row.fieldSettingId == null) {
      return 'Выберите стикер или удалите пустую строку'
    }

    if (usedFieldSettingIds.has(row.fieldSettingId)) {
      return 'Один стикер можно указать только один раз'
    }

    usedFieldSettingIds.add(row.fieldSettingId)

    const sticker = stickerRows.find(
      (item) => item.fieldSettingId === row.fieldSettingId,
    )

    if (!sticker) {
      return 'Выбранный стикер недоступен на доске'
    }

    if (!hasRecurringTaskStickerDefaultValue(sticker.fieldType, row.value)) {
      return `Укажите значение для стикера «${sticker.title}»`
    }

    if (sticker.fieldType === 'number') {
      const scalarValue = Array.isArray(row.value) ? row.value[0] ?? null : row.value
      const normalized = normalizeTaskTrackerStickerValueForPersist(
        sticker.fieldType,
        scalarValue,
      )

      if (!normalized || !Number.isFinite(Number(normalized))) {
        return `Укажите корректное число для стикера «${sticker.title}»`
      }
    }

    if (sticker.fieldType === 'deadline') {
      const scalarValue = Array.isArray(row.value) ? row.value[0] ?? null : row.value
      const offsetDays = parseRecurringTaskDeadlineDefaultValue(scalarValue)

      if (offsetDays == null) {
        return `Укажите количество дней для дедлайна`
      }
    }
  }

  return null
}

export const formatRecurringTaskStickerDefaultsSummary = (
  defaults: TaskTrackerRecurringTaskStickerDefault[],
): string => {
  const count = defaults.filter(
    (item) =>
      (item.values?.length ?? 0) > 0 ||
      Boolean(normalizeStickerValueForApi(item.value)),
  ).length

  if (count === 0) return 'Стикеры не заданы'

  const suffix =
    count % 10 === 1 && count % 100 !== 11
      ? 'стикер'
      : count % 10 >= 2 &&
          count % 10 <= 4 &&
          (count % 100 < 10 || count % 100 >= 20)
        ? 'стикера'
        : 'стикеров'

  return `${count} ${suffix} по умолчанию`
}

export const resolveRecurringTaskStickerDefaultInitialValue = (
  fieldType: string | undefined,
): RecurringTaskStickerDefaultValue =>
  fieldType === 'performer' ? [] : null
